import { useTranslations } from "use-intl";
import { Button } from "@mui/material";

import type { TFlightFormFieldValues } from "@/app/lib/types/types";
import { TripType } from "@/app/lib/types/enums";

import { Calendar } from "../calendar/Calendar";
import TripTypeRadioGroup from "./TripTypeRadioGroup";
import { DatesGuard } from "./DatesGuard";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { PersistentDrawer } from "../PersistentDrawer";

const NUMBER_OF_MONTHS = 12;

type Props = {
  initialValues: TFlightFormFieldValues;
  onConfirm: (values: TFlightFormFieldValues) => void;
};

function DatesSelection({ initialValues, onConfirm }: Props) {
  const t = useTranslations();

  const methods = useForm<TFlightFormFieldValues>({
    defaultValues: initialValues,
  });

  const { control, handleSubmit, setValue } = methods;

  const [tripType, departure, ret] = useWatch({
    control,
    name: ["tripType", "dates.departure", "dates.return"],
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onConfirm)}
        className="flex min-h-0 shrink grow basis-auto flex-col"
      >
        <Controller
          name="tripType"
          control={control}
          render={({ field }) => (
            <TripTypeRadioGroup value={field.value} onChange={field.onChange} />
          )}
        />

        <Calendar
          allowSameDayRangeSelection
          startField={{
            label: t("flights.search.departureDate"),
            value: departure,
            path: "dates.departure",
          }}
          endField={
            tripType === TripType.ROUND_TRIP
              ? {
                  label: t("flights.search.returnDate"),
                  value: ret,
                  path: "dates.return",
                }
              : undefined
          }
          setFieldValue={(path, value) => {
            setValue(path as "dates.departure" | "dates.return", value, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          }}
          disabledDates={{ before: new Date() }}
          startMonth={new Date()}
          numberOfMonths={NUMBER_OF_MONTHS}
        />
        <PersistentDrawer>
          <Button
            fullWidth
            variant="contained"
            size="medium"
            sx={{
              marginTop: "auto",
            }}
            type="submit"
          >
            {tripType === TripType.ROUND_TRIP
              ? t("calendar.confirmDates")
              : t("calendar.confirmDate")}
          </Button>
        </PersistentDrawer>
      </form>
      <DatesGuard />
    </FormProvider>
  );
}

export default DatesSelection;
